import { Check, Copy } from 'lucide-react'
import * as React from 'react'
import { cn } from '../../lib/cn'
import { Icon } from './Icon'

/**
 * CopyButton — click to copy a string value to clipboard. Pencil NeB3w.
 */
export interface CopyButtonProps
  extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'value'> {
  value: string
  label?: string
  /** optional render override for success toast/label */
  onCopied?: () => void
}

export const CopyButton = React.forwardRef<HTMLButtonElement, CopyButtonProps>(
  ({ className, value, label = 'Copy', onCopied, ...props }, ref) => {
    const [copied, setCopied] = React.useState(false)
    const handleClick = React.useCallback(async () => {
      try {
        await navigator.clipboard.writeText(value)
        setCopied(true)
        onCopied?.()
        window.setTimeout(() => setCopied(false), 1500)
      } catch {
        // clipboard failed — silent
      }
    }, [value, onCopied])
    return (
      <button
        ref={ref}
        type="button"
        onClick={handleClick}
        aria-label={copied ? 'Copied' : label}
        className={cn(
          // Circle hover state — consistent with every icon-only button in the DS
          'inline-flex items-center justify-center rounded-pill h-8 w-8 text-ia-text-tertiary transition-all',
          'hover:bg-[var(--color-ia-sidebar-active-glow)] hover:text-[var(--color-ia-sidebar-active-fg)]',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background',
          className,
        )}
        {...props}
      >
        <Icon icon={copied ? Check : Copy} size="sm" />
      </button>
    )
  },
)
CopyButton.displayName = 'CopyButton'
